import {titleCase} from 'title-case';

import {LocalizeRenderer} from '../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../libs/shared/core/src/helpers/internationalization/message';
import {MessageTypesEnum} from '../../../libs/shared/core/src/models/api/commonMessageModel';
import {buildTeammateDisplayName} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {shortcodeToUnified} from '../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  BasicActivityModel,
  BasicActivityTypesEnum,
  CommentActivityTypesEnum,
} from '../../models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../models/api/activitySourceModel';
import {AuditTrailActivity} from '../../models/api/auditTrailActivityModel';
import {NotificationModel} from '../../models/api/notificationModel';
import {baseEmojiValidationMap} from '../../services/emojis/emojis';
import {NotificationOptions, NotificationTypesEnum} from '../../services/notifications';
import {FindTeammate} from '../../store/data/teammates/teammatesSelectors';
import {transformEmojis} from '../emoji/emojiAndEmoticonsTextHelpers';

type ActivityWrapper = NotificationModel | AuditTrailActivity;
export function buildActivityDisplay(
  localize: Localizer,
  findTeammate: FindTeammate,
  activityWrapper: ActivityWrapper,
) {
  const {activity} = activityWrapper;
  switch (activity.visualType) {
    case ActivityVisualTypesEnum.COMMENT:
      return buildActivityCommentDisplay(localize, findTeammate, activityWrapper);

    case ActivityVisualTypesEnum.MESSAGE:
      return buildActivityMessageDisplay(localize, activityWrapper);

    case ActivityVisualTypesEnum.BASIC:
      return buildActivityBasicDisplay(localize, activityWrapper, activity);

    default:
      return null;
  }
}

/*
 * Comment.
 */

function buildActivityCommentDisplay(
  localize: Localizer,
  findTeammate: FindTeammate,
  activityWrapper: ActivityWrapper,
): NotificationOptions {
  // Find the teammate avatar to display, if any.
  const {activity, fromTeammateId, summary} = activityWrapper;
  const author = (fromTeammateId && findTeammate(fromTeammateId)) || undefined;
  const authorName = (author && buildTeammateDisplayName(author)) || activityWrapper.fromName;
  const authorAvatarUrl = author && author.avatarUrl;

  // Build the notification options.
  return {
    title: buildActivityCommentTitle(localize, activity, authorName),
    body:
      summary &&
      transformEmojis(summary, baseEmojiValidationMap, (_key, _image, emoji) =>
        shortcodeToUnified(emoji),
      ).join(''),
    iconUrl: authorAvatarUrl,
  };
}

function buildActivityCommentTitle(localize: Localizer, activity: ActivityModel, authorName: string) {
  // build the title.
  switch (activity.type) {
    case CommentActivityTypesEnum.MENTION:
      return localize(
        defineMessage({
          id: 'notification-title-mention',
          message: `Mentioned by ${authorName}`,
        }),
      );

    default:
      return localize(
        defineMessage({
          id: 'notification-title-comment',
          message: `Comment from ${authorName}`,
        }),
      );
  }
}

/*
 * Message.
 */

function buildActivityMessageDisplay(
  localize: Localizer,
  activityWrapper: ActivityWrapper,
): NotificationOptions {
  return {
    title: buildActivityMessageTitle(localize, activityWrapper),
    body: activityWrapper.summary,
    type: getMessageNotificationType(activityWrapper.messageType),
  };
}

const getMessageNotificationType = (messageType: MessageTypesEnum | undefined) =>
  messageType === MessageTypesEnum.FRONT_CHAT ? NotificationTypesEnum.FRONT_CHAT : undefined;

function buildActivityMessageTitle(localize: Localizer, activityWrapper: ActivityWrapper) {
  const {fromName, messageType} = activityWrapper;
  // We need a Message Type.
  if (!messageType) {
    throw new Error('A message type was not provided for the specified message activity.');
  }

  // Build the localized version of the Message Type.
  const displayMessageType = titleCase(messageTypesRenderer[messageType](localize));

  // If no recipient name was provided, render a generic message.
  if (!fromName) {
    return displayMessageType;
  }

  // Otherwise, include the name.
  return localize(
    defineMessage({
      id: 'notification-title-message',
      message: `${displayMessageType} from ${fromName}`,
    }),
  );
}

type MessageTypesRenderer = {[T in MessageTypesEnum]: LocalizeRenderer};
const messageTypesRenderer: MessageTypesRenderer = {
  [MessageTypesEnum.INTERNAL]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-internal',
        message: 'internal chat',
      }),
    ),
  [MessageTypesEnum.EMAIL]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-email',
        message: 'email',
      }),
    ),
  [MessageTypesEnum.TWEET]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-tweet',
        message: 'post',
      }),
    ),
  [MessageTypesEnum.TWEET_DM]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-twitterdm',
        message: 'dm',
      }),
    ),
  [MessageTypesEnum.CALL]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-call',
        message: 'phone call',
      }),
    ),
  [MessageTypesEnum.SMS]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-sms',
        message: 'sms',
      }),
    ),
  [MessageTypesEnum.GOOGLE_PLAY]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-googleplay',
        message: 'google play review',
      }),
    ),
  [MessageTypesEnum.FACEBOOK]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-facebook',
        message: 'facebook message',
      }),
    ),
  [MessageTypesEnum.INTERCOM]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-intercom',
        message: 'intercom message',
      }),
    ),
  [MessageTypesEnum.SMOOCH]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-smooch',
        message: 'smooch message',
      }),
    ),
  [MessageTypesEnum.LAYER]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-layer',
        message: 'chat',
      }),
    ),
  [MessageTypesEnum.YALO_WHATSAPP]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-whatsapp',
        message: 'whatsapp message',
      }),
    ),
  [MessageTypesEnum.WHATSAPP]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-whatsapp',
        message: 'whatsapp message',
      }),
    ),
  [MessageTypesEnum.CUSTOM]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-custom',
        message: 'custom message',
      }),
    ),
  [MessageTypesEnum.FRONT_CHAT]: (localize) =>
    localize(
      defineMessage({
        id: 'message-type-frontchat',
        message: 'chat',
      }),
    ),
};

/*
 * Basic.
 */

function buildActivityBasicDisplay(
  localize: Localizer,
  activityWrapper: ActivityWrapper,
  activity: BasicActivityModel,
): NotificationOptions | null {
  const title = buildActivityBasicTitle(localize, activity);
  if (!title) {
    return null;
  }

  const {fromName} = activityWrapper;
  // If no recipient name was provided, render a generic message.
  if (!fromName) {
    return {
      title,
      body: activityWrapper.summary,
    };
  }

  // Otherwise, include the name.
  return {
    title: localize(
      defineMessage({
        id: 'notification-title-message',
        message: `${title} by ${fromName}`,
      }),
    ),
    body: activityWrapper.summary,
  };
}

function buildActivityBasicTitle(localize: Localizer, activity: BasicActivityModel) {
  switch (activity.type) {
    case BasicActivityTypesEnum.ASSIGN:
      return localize(
        defineMessage({
          id: 'notification-title-assign',
          message: 'Conversation assigned',
        }),
      );

    case BasicActivityTypesEnum.UNASSIGN:
      return localize(
        defineMessage({
          id: 'notification-title-unassign',
          message: 'Conversation unassigned',
        }),
      );

    case BasicActivityTypesEnum.ARCHIVE:
      return localize(
        defineMessage({
          id: 'notification-title-close',
          message: 'Conversation archived',
        }),
      );

    case BasicActivityTypesEnum.REOPEN:
      return localize(
        defineMessage({
          id: 'notification-title-reopen',
          message: 'Conversation reopened',
        }),
      );

    case BasicActivityTypesEnum.REMINDER:
    case BasicActivityTypesEnum.TRACKER_REMINDER:
      return localize(
        defineMessage({
          id: 'notification-title-reminder',
          message: 'Conversation reminder',
        }),
      );

    case BasicActivityTypesEnum.RULE_NOTIFY:
      return buildRuleTitle(localize, activity);

    case BasicActivityTypesEnum.MOVE:
      return localize(
        defineMessage({
          id: 'notification-title-move',
          message: 'Conversation moved into inbox',
        }),
      );

    case BasicActivityTypesEnum.TRASH:
      return localize(
        defineMessage({
          id: 'notification-title-trash',
          message: 'Conversation trashed',
        }),
      );

    case BasicActivityTypesEnum.TRACKER_SHARE:
      return localize(
        defineMessage({
          id: 'notification-title-share',
          message: `Invited to conversation`,
        }),
      );

    default:
      return null;
  }
}

function buildRuleTitle(localize: Localizer, activity: BasicActivityModel) {
  // Find the name of the rule that created the activity.
  const {source} = activity;
  const ruleName = source.visualType === ActivitySourceVisualTypesEnum.RULE && source.ruleName;

  // If a name could not found, render a generic message.
  if (!ruleName) {
    return localize(
      defineMessage({
        id: 'notification-title-rule-generic',
        message: 'Notification from rule',
      }),
    );
  }

  // Otherwise, include the rule name.
  return localize(
    defineMessage({
      id: 'notification-title-rule',
      message: `Notification from ${ruleName}`,
    }),
  );
}
